import React, { useState, useEffect, useCallback } from "react";
import {
  Button,
  Table,
  Form,
  Spinner,
  Alert,
  Badge,
} from "react-bootstrap";
import PerfectScrollbar from "react-perfect-scrollbar";
import Swal from "sweetalert2";
import * as XLSX from "xlsx";

import {
  getPuntuacionesByFecha,
  getPromedioProfesionales,
  getPromedioByFecha,
} from "../../../api/puntuacionApi";

const Puntuacion = () => {
  const [puntuaciones, setPuntuaciones] = useState([]);
  const [promedios, setPromedios] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingPromedio, setLoadingPromedio] = useState(false);
  const [showPromedio, setShowPromedio] = useState(false);
  const [promedioMode, setPromedioMode] = useState(null); // "global" | "fecha"
  const [filter, setFilter] = useState({
    fechainicio: "",
    fechafin: "",
    ProfesionalText: "",
    PacienteText: "",
  });

  // === Buscar puntuaciones solo al filtrar ===
  const fetchPuntuaciones = useCallback(async () => {
    if (!filter.fechainicio || !filter.fechafin) {
      Swal.fire("Atención", "Debe seleccionar un rango de fechas", "info");
      return;
    }

    try {
      setLoading(true);
      let data = await getPuntuacionesByFecha(filter.fechainicio, filter.fechafin);

      // Filtro por texto de profesional (busca en p.Profesional o p.Usuario)
      if (filter.ProfesionalText && filter.ProfesionalText.trim() !== "") {
        const pt = filter.ProfesionalText.toLowerCase();
        data = (data || []).filter((p) => {
          const nombreProf = (p.Profesional || p.Usuario || "").toString().toLowerCase();
          return nombreProf.includes(pt);
        });
      }

      // Filtro por texto de paciente (busca en p.Paciente o en otros campos si aplica)
      if (filter.PacienteText && filter.PacienteText.trim() !== "") {
        const pat = filter.PacienteText.toLowerCase();
        data = (data || []).filter((p) => {
          const nombrePac = (p.Paciente || "").toString().toLowerCase();
          return nombrePac.includes(pat);
        });
      }

      setPuntuaciones(data || []);
      // cuando mostramos la tabla de puntuaciones, no tocar el modo promedios
      setShowPromedio(false);
    } catch (err) {
      Swal.fire("Error", "No se pudieron cargar las puntuaciones", "error");
    } finally {
      setLoading(false);
    }
  }, [filter]);

  // === Promedios ===
  const fetchPromediosGlobales = async () => {
    try {
      setLoadingPromedio(true);
      const data = await getPromedioProfesionales();
      setPromedios(data || []);
      setShowPromedio(true);
      setPromedioMode("global");
    } catch (err) {
      Swal.fire("Error", "No se pudieron obtener los promedios globales", "error");
    } finally {
      setLoadingPromedio(false);
    }
  };

  const fetchPromediosPorFecha = async () => {
    if (!filter.fechainicio || !filter.fechafin) {
      Swal.fire("Atención", "Debe seleccionar un rango de fechas", "info");
      return;
    }
    try {
      setLoadingPromedio(true);
      const data = await getPromedioByFecha(filter.fechainicio, filter.fechafin);
      setPromedios(data || []);
      setShowPromedio(true);
      setPromedioMode("fecha");
    } catch (err) {
      Swal.fire("Error", "No se pudieron obtener los promedios por fecha", "error");
    } finally {
      setLoadingPromedio(false);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilter((prev) => ({ ...prev, [name]: value }));
  };

  // === Exportar a Excel (genérica) ===
  const exportToExcel = (data, filename) => {
    if (!data || data.length === 0) {
      Swal.fire("Sin datos", "No hay información para exportar", "info");
      return;
    }
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Datos");
    XLSX.writeFile(wb, `${filename}.xlsx`);
  };

  // Exportaciones específicas para cada tabla (mejorar encabezados)
  const safeFilenamePart = (s) =>
    (s || "")
      .toString()
      .trim()
      .replace(/\s+/g, "_")
      .replace(/[^a-zA-Z0-9_\-]/g, "")
      .slice(0, 40);

  const exportPuntuaciones = () => {
    const mapped = puntuaciones.map((p) => ({
      Profesional: p.Profesional || p.Usuario || "-",
      Paciente: p.Paciente || "-",
      Puntuacion: p.Puntuacion,
      Comentario: p.Comentario || "-",
      Fecha: p.FechaCreacion
        ? new Date(p.FechaCreacion).toLocaleString("es-ES", {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
          })
        : "-",
      Legajo: p.IdLegajoEnfermeria || "",
    }));
    const fn = `Puntuaciones_${filter.fechainicio || "all"}_${filter.fechafin || "all"}_${safeFilenamePart(
      filter.ProfesionalText
    )}_${safeFilenamePart(filter.PacienteText)}`;
    exportToExcel(mapped, fn);
  };

  const exportPromedios = () => {
    const mapped = promedios.map((p, i) => ({
      Orden: i + 1,
      Profesional: p.Profesional || p.Usuario || "-",
      Promedio: Number(p.Promedio || 0).toFixed(2),
      TotalCalificaciones: p.TotalCalificaciones || p.Cantidad || 0,
    }));
    const fn = `Promedios_${promedioMode === "fecha" ? `${filter.fechainicio || "start"}_${filter.fechafin || "end"}` : "global"}`;
    exportToExcel(mapped, fn);
  };

  // === Solo efectos mínimos ===
  useEffect(() => {
    // No cargamos listas para selects porque ahora usamos inputs de texto
  }, []);

  // ----- Styles -----
  const filterCardStyle = {
    backgroundColor: "#f8f9fa",
    borderRadius: 8,
    padding: "1rem",
  };

  const tableCardHeader = {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "0.75rem",
  };

  const puntuacionesHeaderStyle = {
    background: "linear-gradient(90deg,#304a97,#2b6fb5)",
    color: "white",
    padding: "0.5rem 0.75rem",
    borderRadius: 6,
  };

  const promediosHeaderStyle = {
    background: "linear-gradient(90deg,#007b83,#12929e)",
    color: "white",
    padding: "0.5rem 0.75rem",
    borderRadius: 6,
  };

  const exportBtnStyle = {
    minWidth: 44,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
  };

  // Helpers para títulos dinámicos
  const puntuacionesTitle = filter.fechainicio && filter.fechafin
    ? `Puntuaciones entre ${filter.fechainicio} y ${filter.fechafin}`
    : "Puntuaciones";

  const promediosTitle =
    promedioMode === "fecha"
      ? `Promedios entre ${filter.fechainicio || ""} y ${filter.fechafin || ""}`.trim()
      : promedioMode === "global"
      ? "Promedios Globales"
      : "Promedios";

  return (
    <div className="card-body">
      {/* === FILTROS === */}
      <div className="d-flex flex-wrap align-items-end gap-3 mb-3" style={filterCardStyle}>
        <Form.Group style={{ minWidth: 180 }}>
          <Form.Label className="fw-semibold">Desde</Form.Label>
          <Form.Control
            type="date"
            name="fechainicio"
            value={filter.fechainicio}
            onChange={handleFilterChange}
          />
        </Form.Group>

        <Form.Group style={{ minWidth: 180 }}>
          <Form.Label className="fw-semibold">Hasta</Form.Label>
          <Form.Control
            type="date"
            name="fechafin"
            value={filter.fechafin}
            onChange={handleFilterChange}
          />
        </Form.Group>

        <Form.Group style={{ minWidth: 220 }}>
          <Form.Label className="fw-semibold">Profesional</Form.Label>
          <Form.Control
            type="text"
            name="ProfesionalText"
            value={filter.ProfesionalText}
            onChange={handleFilterChange}
          />
        </Form.Group>

        <Form.Group style={{ minWidth: 220 }}>
          <Form.Label className="fw-semibold">Usuario </Form.Label>
          <Form.Control
            type="text"
            name="PacienteText"
          
            value={filter.PacienteText}
            onChange={handleFilterChange}
          />
        </Form.Group>

        <div
          className="d-flex flex-wrap gap-2"
          style={{ marginTop: "auto", marginBottom: 2 }}
        >
          <Button variant="primary" onClick={fetchPuntuaciones} disabled={loading}>
            {loading ? (
              <>
                <Spinner animation="border" size="sm" /> <span className="ms-1">Buscando...</span>
              </>
            ) : (
              "Filtrar"
            )}
          </Button>

          <Button
            variant="outline-primary"
            onClick={fetchPromediosGlobales}
            disabled={loadingPromedio}
            title="Promedios globales"
          >
            {loadingPromedio ? (
              <>
                <Spinner animation="border" size="sm" /> <span className="ms-1">Cargando...</span>
              </>
            ) : (
              "Promedios Globales"
            )}
          </Button>

          <Button
            variant="outline-info"
            onClick={fetchPromediosPorFecha}
            disabled={loadingPromedio}
            title="Promedios en el rango seleccionado"
          >
            {loadingPromedio ? (
              <>
                <Spinner animation="border" size="sm" /> <span className="ms-1">Cargando...</span>
              </>
            ) : (
              "Promedios por Fecha"
            )}
          </Button>
        </div>
      </div>

      {/* === TABLA DE PUNTUACIONES (con su propio header y export) === */}
      <div style={{ marginBottom: 18 }}>
        <div style={tableCardHeader}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={puntuacionesHeaderStyle}>
              <strong>{puntuacionesTitle}</strong>
              <div style={{ fontSize: 12, opacity: 0.9 }}>
                {filter.ProfesionalText ? `Profesional: ${filter.ProfesionalText}` : ""}
                {filter.ProfesionalText && filter.PacienteText ? " • " : ""}
                {filter.PacienteText ? `Paciente: ${filter.PacienteText}` : ""}
              </div>
            </div>
            <div>
              <small className="text-muted">
                Registros: <strong>{puntuaciones.length}</strong>
              </small>
            </div>
          </div>

          <div>
            <Button
              variant="success"
              size="sm"
              className="me-2"
              onClick={exportPuntuaciones}
              style={exportBtnStyle}
              title="Exportar puntuaciones a Excel"
            >
              Excel
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-4">
            <Spinner animation="border" />
          </div>
        ) : puntuaciones.length === 0 ? (
          <Alert variant="warning">No hay registros de puntuación.</Alert>
        ) : (
          <PerfectScrollbar>
            <Table responsive bordered striped className="align-middle">
              <thead style={{ backgroundColor: "#304a97", color: "white" }}>
                <tr>
                  <th style={{ width: "50px" }}>Ver</th>
                  <th>Profesional</th>
                  <th>Paciente</th>
                  <th>Puntuación</th>
                  <th>Comentario</th>
                  <th>Fecha</th>
                </tr>
              </thead>
              <tbody>
                {puntuaciones.map((p, i) => (
                  <tr key={i}>
                    <td className="text-center">
                      {p.IdLegajoEnfermeria ? (
                        <a
                          href={`/legajo/${p.IdLegajoEnfermeria}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-link p-0"
                          title="Ver Legajo"
                        >
                          <i className="fa fa-eye" style={{ color: "#304a97", fontSize: "18px" }} />
                        </a>
                      ) : (
                        "-"
                      )}
                    </td>
                    <td>{p.Profesional || p.Usuario || "-"}</td>
                    <td>{p.Paciente || "-"}</td>
                    <td>
                      <Badge bg="primary" pill>
                        {p.Puntuacion}
                      </Badge>
                    </td>
                    <td style={{ maxWidth: 320, whiteSpace: "pre-wrap" }}>{p.Comentario || "-"}</td>
                    <td>
                      {p.FechaCreacion
                        ? new Date(p.FechaCreacion).toLocaleDateString("es-ES", {
                            year: "numeric",
                            month: "2-digit",
                            day: "2-digit",
                            hour: "2-digit",
                            minute: "2-digit",
                          })
                        : "-"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </PerfectScrollbar>
        )}
      </div>

      {/* === TABLA DE PROMEDIOS (cada tabla con su propio export y mejor estética) === */}
      {showPromedio && (
        <div className="mt-4">
          <div style={tableCardHeader}>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <div style={promediosHeaderStyle}>
                <strong>{promediosTitle}</strong>
                <div style={{ fontSize: 12, opacity: 0.95 }}>Consulta de promedios por profesional</div>
              </div>
              <div>
                <small className="text-muted">
                  Profesionales: <strong>{promedios.length}</strong>
                </small>
              </div>
            </div>

            <div>
              <Button
                variant="success"
                size="sm"
                onClick={exportPromedios}
                style={exportBtnStyle}
                title="Exportar promedios a Excel"
              >
                Excel
              </Button>
            </div>
          </div>

          {promedios.length === 0 ? (
            <Alert variant="primary">No hay promedios para mostrar.</Alert>
          ) : (
            <PerfectScrollbar>
              <Table responsive bordered className="align-middle">
                <thead style={{ backgroundColor: "#007b83", color: "white" }}>
                  <tr>
                    <th style={{ width: 60 }}>#</th>
                    <th>Profesional</th>
                    <th style={{ width: 160 }}>Promedio</th>
                    <th style={{ width: 180 }}>Total Calificaciones</th>
                  </tr>
                </thead>
                <tbody>
                  {promedios.map((p, i) => {
                    const promedioNum = Number(p.Promedio || 0);
                    const percent = Math.max(0, Math.min(100, (promedioNum / 5) * 100));
                    return (
                      <tr key={i}>
                        <td>{i + 1}</td>
                        <td style={{ minWidth: 220 }}>{p.Profesional || p.Usuario || "-"}</td>
                        <td>
                          <strong style={{ fontSize: 16 }}>{promedioNum.toFixed(2)}</strong>
                        </td>
                        <td>
                          <Badge bg="info" pill>
                            {p.TotalCalificaciones || p.Cantidad || 0}
                          </Badge>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </PerfectScrollbar>
          )}
        </div>
      )}
    </div>
  );
};

export default Puntuacion;